import { ClaimNode, SeasonPassReward, SeasonPassRarity, SeasonId } from '../types/seasonPass';

// Import game images (must be at top per eslint import/first)
import myFavoriteMusiciansImg from '../../../shared/assets/images/games/my_favorite_musicians.png';
import rhythmRallyImg from '../../../shared/assets/images/games/Rhythm Rally Thumbnail.png';
import rhythmPuzzleImg from '../../../shared/assets/images/activities/rhythm-puzzle.png';
import beatDecodersImg from '../../../shared/assets/images/games/Beat Decoder Thumbnail.png';
import rocketRaceImg from '../../../shared/assets/images/games/rocketrace.png';
import harmonyGuesserImg from '../../../shared/assets/images/games/harmony_guesser.png';
import chordstructionImg from '../../../shared/assets/images/games/Chordstruction Thumbnail.png';
import scaleTheTowerImg from '../../../shared/assets/images/games/Scale The Tower Thumbnail.png';
import yeetTheScoopImg from '../../../shared/assets/images/games/Yeet The Scoop Thumbnail.png';
import pianoClickerImg from '../../../shared/assets/images/games/Piano Clicker Thumbnail.png';
import harmonyVotersImg from '../../../shared/assets/images/games/Harmonidome Student Thumbnail.png';
import tuneTestersImg from '../../../shared/assets/images/games/tune_testers.png';
import tunaImg from '../../../shared/assets/images/games/Tuna Thumbnail.png';
import stepSkipRepeatImg from '../../../shared/assets/images/games/Step_Skip_Repeat.png';
import melodyShaperImg from '../../../shared/assets/images/games/Melody_Shaper.png';
import melopedesImg from '../../../shared/assets/images/games/Melopede Caves Thumbnail.png';
// import keySmashersImg from '../../../shared/assets/images/games/key_smashers.png'; // Removed - no longer available
import reactAttackImg from '../../../shared/assets/images/games/React Attack Student Thumbnail.png';
import realOrAiImg from '../../../shared/assets/images/games/Real Or AI Thumbnail.png';
import flappyMuseImg from '../../../shared/assets/images/games/flappy_muse.png';
import { 
  Incredibox as incrediboxImg,
  KahootRhythm as kahootImg,
  InstrumentFamilies as instrumentFamiliesImg,
  InstrumentsInVideos as instrumentsInVideosImg,
  Rhythm101 as insideOrchestraImg
} from '../../../shared/assets/images/activities';
import overtureMusicLabImg from '../../../shared/assets/images/games/Overture Music Lab Thumbnail.png';
import soundGymImg from '../../../shared/assets/images/activities/soundgym.png';
import sunoImg from '../../../shared/assets/images/activities/Suno_activity.png';
// Images for SoundGym/Suno are referenced via the registry mapping and activity images where needed

// Import rarity images (must be at top per eslint import/first)
import commonGem from '../../../shared/assets/images/rarity/common.png';
import uncommonGem from '../../../shared/assets/images/rarity/uncommon.png';
import rareGem from '../../../shared/assets/images/rarity/rare.png';
import epicGem from '../../../shared/assets/images/rarity/epic.png';
import legendaryGem from '../../../shared/assets/images/rarity/legendary.png';

// Import game registry to access game properties
import { GAME_REGISTRY } from '../../games/models/GameRegistry';

// Utility function to get rarity based on game properties (single source of truth)
const getGameRarity = (gameId: string): SeasonPassRarity => {
  const game = GAME_REGISTRY[gameId];
  if (!game) {
    return 'common'; // Default for unknown games
  }

  // Map game properties to rarity
  // Priority: tier > difficulty > category
  if (game.tier === 'premium') {
    return 'legendary';
  }

  if (game.tier === 'core') {
    // Core games: difficulty determines rarity
    switch (game.difficulty) {
      case 'hard': return 'epic';
      case 'medium': return 'rare';
      case 'easy': return 'uncommon';
      default: return 'uncommon';
    }
  }

  if (game.tier === 'basic') {
    // Basic games: mostly common with some variation
    switch (game.difficulty) {
      case 'hard': return 'rare';
      case 'medium': return 'uncommon';
      case 'easy': return 'common';
      default: return 'common';
    }
  }

  return 'common'; // Default fallback
};


// Game-to-image mapping based on existing assets
const gameImageMap: Record<string, string> = {
  'my-favorite-musicians': myFavoriteMusiciansImg,
  'rhythm-rally': rhythmRallyImg,
  'rhythm-puzzle': rhythmPuzzleImg,
  'beat-decoders': beatDecodersImg,
  'rocket-race': rocketRaceImg,
  'harmony-guesser': harmonyGuesserImg,
  'yeet-the-scoop': yeetTheScoopImg,
  'piano-clicker': pianoClickerImg,
  'chordstruction': chordstructionImg,
  'scale-the-tower': scaleTheTowerImg,
  'harmonidome': harmonyVotersImg,
  'tune-testers': tuneTestersImg,
  'step-skip-repeat': stepSkipRepeatImg,
  'melody-shaper': melodyShaperImg,
  'melopedes': melopedesImg,
  // 'key-smashers': keySmashersImg, // Removed - no longer available
  'tuna': tunaImg,
  'react-attack-student': reactAttackImg,
  'real-or-ai': realOrAiImg,
  'flappy-muse': flappyMuseImg,
  'overture-music-lab': overtureMusicLabImg,
  'incredibox': incrediboxImg,
  'kahoot-intro': kahootImg,
  'instrument-families-explore': instrumentFamiliesImg,
  'instruments-in-videos': instrumentsInVideosImg,
  'inside-the-orchestra': insideOrchestraImg,
  'soundgym-eq': soundGymImg,
  'suno-ai': sunoImg
};

// Rarity gem mapping
export const rarityGemMap: Record<SeasonPassRarity, string> = {
  common: commonGem,
  uncommon: uncommonGem,
  rare: rareGem,
  epic: epicGem,
  legendary: legendaryGem
};

// Helper function to create rewards
const createReward = (gameId: string, name: string): SeasonPassReward => ({
  id: `reward_${gameId}`,
  type: 'game',
  gameId,
  name,
  image: gameImageMap[gameId] || '/src/shared/assets/images/games/default-game.png',
  rarity: getGameRarity(gameId),
  description: `Unlock ${name} to play anytime!`
});

// Season 1 Claim Nodes Configuration
export const season1ClaimNodes: ClaimNode[] = [
  // Start Node (Position 0)
  {
    id: 'start_node',
    seasonId: 'season1',
    position: 0,
    type: 'start',
    title: 'Welcome to Season 1!',
    rewards: [
      createReward('overture-music-lab', 'Overture Music Lab')
    ],
    requirements: {
      type: 'always_unlocked'
    }
  },

  // Before Level 1 (Position 1)
  {
    id: 'pre_level_1',
    seasonId: 'season1',
    position: 1,
    type: 'level_reward',
    title: 'Rhythm Foundation',
    rewards: [
      createReward('kahoot-intro', 'Kahoot: Intro to Music'),
      createReward('my-favorite-musicians', 'My Favorite Musicians'),
      createReward('rhythm-rally', 'Rhythm Rally'),
      createReward('rhythm-puzzle', 'Rhythm Puzzle'),
      createReward('overture-music-lab', 'Overture Music Lab')
    ],
    requirements: {
      type: 'node_claimed',
      nodeId: 'start_node'
    }
  },

  // After Level 1 - Before Level 2 (Position 2)
  {
    id: 'post_level_1',
    seasonId: 'season1',
    position: 2,
    type: 'level_reward',
    levelId: '1',
    title: 'Advanced Rhythm Tools',
    rewards: [
      createReward('beat-decoders', 'Beat Decoders'),
      createReward('rocket-race', 'Rocket Race')
    ],
    requirements: {
      type: 'level_complete',
      levelId: '1'
    }
  },

  // After Level 2 - Before Level 3 (Position 3)
  {
    id: 'post_level_2',
    seasonId: 'season1',
    position: 3,
    type: 'level_reward',
    levelId: '2',
    title: 'Harmony Discovery',
    rewards: [
      createReward('yeet-the-scoop', 'Yeet the Scoop'),
      createReward('chordstruction', 'Chordstruction'),
      createReward('scale-the-tower', 'Scale the Tower')
    ],
    requirements: {
      type: 'level_complete',
      levelId: '2'
    }
  },

  // After Level 3 - Before Level 4 (Position 4)
  {
    id: 'post_level_3',
    seasonId: 'season1',
    position: 4,
    type: 'level_reward',
    levelId: '3',
    title: 'Advanced Harmony',
    rewards: [
      createReward('harmonidome', 'Harmonidome'),
      createReward('piano-clicker', 'Piano Clicker')
    ],
    requirements: {
      type: 'level_complete',
      levelId: '3'
    }
  },

  // After Level 4 - Before Level 5 (Position 5)
  {
    id: 'post_level_4',
    seasonId: 'season1',
    position: 5,
    type: 'level_reward',
    levelId: '4',
    title: 'Instrumental Mastery',
    rewards: [
      createReward('incredibox', 'Incredibox'),
      createReward('instrument-families-explore', 'Instrument Families'),
      createReward('instruments-in-videos', 'Instruments in Videos')
    ],
    requirements: {
      type: 'level_complete',
      levelId: '4'
    }
  },

  // After Level 5 - Before Level 6 (Position 6)
  {
    id: 'post_level_5',
    seasonId: 'season1',
    position: 6,
    type: 'level_reward',
    levelId: '5',
    title: 'Orchestration Tools',
    rewards: [
      // Level 6 doesn't introduce new unique games, skipping for now
    ],
    requirements: {
      type: 'level_complete',
      levelId: '5'
    }
  },

  // After Level 6 - Before Level 7 (Position 7)
  {
    id: 'post_level_6',
    seasonId: 'season1',
    position: 7,
    type: 'level_reward',
    levelId: '6',
    title: 'Melody Basics',
    rewards: [
      createReward('tune-testers', 'Tune Testers'),
      createReward('step-skip-repeat', 'Step, Skip, or Repeat'),
      createReward('melody-shaper', 'Melody Shaper'),
      createReward('melopedes', 'Melopedes'),
      // Moved Key Smashers and Tuna to post_level_7
    ],
    requirements: {
      type: 'level_complete',
      levelId: '6'
    }
  },

  // After Level 7 - Before Level 8 (Position 8)
  {
    id: 'post_level_7',
    seasonId: 'season1',
    position: 8,
    type: 'level_reward',
    levelId: '7',
    title: 'Melody Composition',
    rewards: [
      // createReward('key-smashers', 'Key Smashers'), // Removed - no longer available
      createReward('tuna', 'Tuna')
    ],
    requirements: {
      type: 'level_complete',
      levelId: '7'
    }
  },

  // After Level 8 - Before Level 9 (Position 9)
  {
    id: 'post_level_8',
    seasonId: 'season1',
    position: 9,
    type: 'level_reward',
    levelId: '8',
    title: 'Musical Form',
    rewards: [
      createReward('react-attack-student', 'React Attack')
    ],
    requirements: {
      type: 'level_complete',
      levelId: '8'
    }
  },

  // After Level 9 - Before Level 10 (Position 10)
  {
    id: 'post_level_9',
    seasonId: 'season1',
    position: 10,
    type: 'level_reward',
    levelId: '9',
    title: 'Dynamics Control',
    rewards: [
      createReward('flappy-muse', 'Flappy Muse'),
      createReward('inside-the-orchestra', 'Inside the Orchestra'),
      createReward('soundgym-eq', 'SoundGym EQ Game')
    ],
    requirements: {
      type: 'level_complete',
      levelId: '9'
    }
  },

  // After Level 10 - Before Level 11 (Position 11)
  {
    id: 'post_level_10',
    seasonId: 'season1',
    position: 11,
    type: 'level_reward',
    levelId: '10',
    title: 'Genre Mastery',
    rewards: [
      createReward('real-or-ai', 'Real or AI'),
      createReward('suno-ai', 'Suno AI')
    ],
    requirements: {
      type: 'level_complete',
      levelId: '10'
    }
  },

  // After Level 11 - Before Level 12 (Position 12)
  {
    id: 'post_level_11',
    seasonId: 'season1',
    position: 12,
    type: 'level_reward',
    levelId: '11',
    title: 'Greatest Hits Collection',
    rewards: [
      // Level 12 is a showcase level using all previous games
    ],
    requirements: {
      type: 'level_complete',
      levelId: '11'
    }
  },

  // Final Completion Node (Position 13)
  {
    id: 'season_complete',
    seasonId: 'season1',
    position: 13,
    type: 'level_reward',
    levelId: '12',
    title: 'Season 1 Complete!',
    rewards: [
      {
        id: 'season1_completion_badge',
        type: 'game',
        gameId: 'season1_badge',
        name: 'Season 1 Master Badge',
        image: '/src/shared/assets/images/rarity/legendary.png',
        rarity: 'legendary',
        description: 'Congratulations! You completed Season 1!'
      }
    ],
    requirements: {
      type: 'level_complete',
      levelId: '12'
    }
  }
];

// Season 2 placeholder for future expansion
export const season2ClaimNodes: ClaimNode[] = [
  // Will be populated when Season 2 content is defined
];

// Export all seasons
export const allSeasonNodes: Record<SeasonId, ClaimNode[]> = {
  season1: season1ClaimNodes,
  season2: season2ClaimNodes
};

// Utility functions
export const getSeasonNodes = (seasonId: SeasonId): ClaimNode[] => {
  return allSeasonNodes[seasonId] || [];
};

export const getNodeById = (nodeId: string, seasonId: SeasonId): ClaimNode | undefined => {
  return getSeasonNodes(seasonId).find(node => node.id === nodeId);
};

export const getNodesForLevel = (levelId: string, seasonId: SeasonId): ClaimNode[] => {
  return getSeasonNodes(seasonId).filter(node => node.levelId === levelId);
};

export const getAllGameIds = (seasonId: SeasonId): string[] => {
  const nodes = getSeasonNodes(seasonId);
  const gameIds = new Set<string>();

  nodes.forEach(node => {
    node.rewards.forEach(reward => {
      if (reward.type === 'game') {
        gameIds.add(reward.gameId);
      }
    });
  });

  return Array.from(gameIds);
};

// Default season configuration
export const DEFAULT_SEASON: SeasonId = 'season1';

// Export the game rarity function as the single source of truth
export { getGameRarity };